export const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export const TIMES = ['Morning (8am–12pm)', 'Afternoon (12pm–4pm)', 'Evening (4pm–8pm)'] as const;
export const DURATIONS = [30, 45, 60] as const;
export const VOICE_STYLES = [
  'Pop / Contemporary',
  'Musical Theater',
  'Classical / Opera',
  'Jazz',
  'Other',
] as const;
export const GRADES = [
  'Pre-K',
  'K',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
  '10th',
  '11th',
  '12th',
  'College',
  'Adult',
] as const;

export const LEVELS: { value: string; label: string; desc: string }[] = [
  { value: 'Standard', label: 'Standard', desc: 'Great for beginners and intermediate students' },
  { value: 'Master', label: 'Master', desc: 'Advanced instruction for serious students' },
  {
    value: 'Director',
    label: 'Director',
    desc: 'Elite coaching from our most experienced faculty',
  },
];

/** Relationship options, split by whether the student is a minor. */
export const MINOR_RELATIONSHIPS = ['Parent', 'Guardian', 'Other'] as const;
export const ADULT_RELATIONSHIPS = ['Self', 'Parent', 'Guardian', 'Other'] as const;

/**
 * One availability "block" — a set of days the student is free at a given
 * time of day. Most families only need one block (e.g. Mon/Wed/Fri
 * afternoons); a second block covers alternates like "or Tuesday evening".
 */
export interface AvailabilityBlock {
  days: string[];
  time: string;
}

export interface StudioLocation {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface ActiveTeacher {
  id: string;
  full_name: string;
  programs: string[] | null;
}

export interface SchoolOption {
  id: string;
  school_name: string;
  county: string | null;
}

export interface FormData {
  // Student
  student_name: string;
  student_dob: string;
  student_email: string;
  current_school: string;
  grade: string;
  is_first_time_participant: string;
  student_is_self: boolean | null;
  // Contact (parent/guardian or self)
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  relationship_to_student: string;
  // Lessons
  instrument: string;
  voice_style: string;
  preferred_location: string;
  requested_start_date: string;
  specific_instructor_request: string;
  teacher_level: string;
  // Availability
  lesson_duration_minutes: number;
  availability: AvailabilityBlock[];
  // Notes
  medical_special_needs: string;
  notes: string;
  /**
   * 'yes' | 'no' | '' where empty means NOT ANSWERED, which is a real state
   * and must never be collapsed into a no. See src/lib/consent/photo-release.
   */
  photo_release: string;
}

export const emptyForm = (): FormData => ({
  student_name: '',
  student_dob: '',
  student_email: '',
  current_school: '',
  grade: '',
  is_first_time_participant: '',
  student_is_self: null,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  relationship_to_student: '',
  instrument: '',
  voice_style: '',
  preferred_location: '',
  requested_start_date: '',
  specific_instructor_request: '',
  teacher_level: '',
  lesson_duration_minutes: 45,
  availability: [{ days: [], time: '' }],
  medical_special_needs: '',
  notes: '',
  photo_release: '',
});

/** Returns integer age from an ISO-ish date string, or null if unparsable. */
export function computeAge(dobIso: string): number | null {
  if (!dobIso) return null;
  const dob = new Date(dobIso + 'T00:00:00');
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

export function isUnder18(dobIso: string): boolean {
  const age = computeAge(dobIso);
  return age !== null && age < 18;
}
